import { useEffect, useState } from 'react';
import { Modal, FormSelect } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { agentApi, type TransferTarget } from '@/shared/api/agentApi';
import { labelFor } from '@/shared/lib/labels';
import { toast } from '@/components/ui/Toast';

/** Group targets by supervisor, with a "ללא צוות" bucket for unsupervised ones. */
export function groupTargetsBySupervisor(
  targets: TransferTarget[],
): { key: string; label: string; items: TransferTarget[] }[] {
  const byName = new Map<number, string>(targets.map((t) => [t.id, t.name]));
  const groups = new Map<string, { label: string; items: TransferTarget[] }>();
  for (const t of targets) {
    const sup = t.supervisor_id ?? null;
    const key = sup == null ? 'none' : String(sup);
    const label = sup == null ? 'ללא צוות' : `צוות ${byName.get(sup) ?? `#${sup}`}`;
    if (!groups.has(key)) groups.set(key, { label, items: [] });
    groups.get(key)!.items.push(t);
  }
  return [...groups.entries()].map(([key, g]) => ({ key, ...g }));
}

export type Intent = 'transfer' | 'conference';

/**
 * Live-call controls for a bridged campaign call: transfer (blind/attended) and
 * add-to-conference. In attended mode the agent joins as a conference and can
 * then complete the transfer by leaving.
 */
export function CallControlBar({ callId, transferAllowed }: { callId: number; transferAllowed: boolean }) {
  const [intent, setIntent] = useState<Intent | null>(null);
  // Once an attended transfer bridges the target in, offer "complete transfer".
  const [attendedJoined, setAttendedJoined] = useState(false);

  if (!transferAllowed) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <button type="button" className="btn" onClick={() => setIntent('transfer')}>
        <Icon name="moveRight" size={14} /> העבר שיחה
      </button>
      <button type="button" className="btn" onClick={() => setIntent('conference')}>
        <Icon name="plus" size={14} /> צרף לשיחה (ועידה)
      </button>
      {attendedJoined && (
        <button
          type="button"
          className="btn primary"
          onClick={async () => {
            try {
              await agentApi.leaveCall(callId);
              toast('ההעברה הושלמה — יצאת מהשיחה', 'ok');
              setAttendedJoined(false);
            } catch (e) {
              toast(msgOf(e, 'שגיאה בהשלמת ההעברה'), 'danger');
            }
          }}
        >
          השלם העברה (צא מהשיחה)
        </button>
      )}

      {intent && (
        <TargetPickerModal
          intent={intent}
          onClose={() => setIntent(null)}
          onSubmit={async (userId, mode) => {
            try {
              if (intent === 'conference') {
                await agentApi.conferenceCall(callId, userId);
                toast('הנציג צורף לשיחה', 'ok');
              } else {
                await agentApi.transferCall(callId, userId, mode);
                if (mode === 'attended') {
                  setAttendedJoined(true);
                  toast('הנציג מצטרף — דבר איתו ואז השלם העברה', 'ok');
                } else {
                  toast('השיחה הועברה', 'ok');
                }
              }
              setIntent(null);
            } catch (e) {
              toast(msgOf(e, 'שגיאה בפעולה'), 'danger');
            }
          }}
        />
      )}
    </div>
  );
}

export function TargetPickerModal({
  intent,
  onClose,
  onSubmit,
}: {
  intent: Intent;
  onClose: () => void;
  onSubmit: (userId: number, mode: 'blind' | 'attended') => Promise<void>;
}) {
  const [targets, setTargets] = useState<TransferTarget[]>([]);
  const [userId, setUserId] = useState('');
  const [mode, setMode] = useState<'blind' | 'attended'>('blind');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void agentApi.transferTargets()
      .then(setTargets)
      .catch(() => toast('שגיאה בטעינת נציגים', 'danger'))
      .finally(() => setLoading(false));
  }, []);

  const groups = groupTargetsBySupervisor(targets);
  const title = intent === 'conference' ? 'צירוף נציג לוועידה' : 'העברת שיחה';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="sm"
      footer={<>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>ביטול</button>
        <button
          type="button"
          className="btn primary"
          disabled={busy || !userId}
          onClick={async () => { setBusy(true); await onSubmit(Number(userId), mode); setBusy(false); }}
        >
          {busy ? '…' : intent === 'conference' ? 'צרף' : 'העבר'}
        </button>
      </>}
    >
      <div className="stack">
        {loading ? (
          <div style={{ color: 'var(--muted)' }}>טוען…</div>
        ) : targets.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין נציגים זמינים כרגע להעברה.</div>
        ) : (
          <>
            <FormSelect value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="בחר נציג">
              <option value="">בחר נציג…</option>
              {groups.map((g) => (
                <optgroup key={g.key} label={g.label}>
                  {g.items.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} · {labelFor('role', t.role)}</option>
                  ))}
                </optgroup>
              ))}
            </FormSelect>

            {intent === 'transfer' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
                  <input type="radio" name="mode" checked={mode === 'blind'} onChange={() => setMode('blind')} />
                  העברה מיידית (ללא הצגה)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
                  <input type="radio" name="mode" checked={mode === 'attended'} onChange={() => setMode('attended')} />
                  העברה מלווה (הצטרפות משותפת ואז השלמה)
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function msgOf(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;
}
